import { Chip } from '@mui/material';
import { useEffect } from 'react';
import { Booking } from '../../api/types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { formatAtStation, stationCalendarDay } from '../../utils/stationTime';
import { useLocationTimeZone } from '../../hooks/useLocationTimeZone';
import { formatBookedWindowLabel } from '../../utils/nonRoomAvailability';
import { fetchLocations } from '../../action/locationsCommands';
import { finishRental, startRental } from '../../action/bookingsCommands';
interface BookingInfoProps {
    booking: Booking;
}

const BookingInfo = (props: BookingInfoProps) => {
    const dispatch = useDispatch();

    const { t } = useTranslation();
    // #3219: the booking's days and times are its location's clock.
    const timeZone = useLocationTimeZone(props.booking.locationId);

    // Startable from the booking's first day on — days at the location.
    const checkIfOrderCanBeStarted = (booking: Booking) => {
        const start = booking?.items[0]?.fromDate ?? booking?.items[0]?.startDate;
        if (!start) return false;
        return stationCalendarDay(new Date(start), timeZone) > stationCalendarDay(new Date(), timeZone);
    };

    const hasBeenStarted = Boolean(
        props.booking?.items[0]?.rental &&
            Object.keys(props.booking?.items[0]?.rental).length > 0,
    );
    const hasBeenFinished = Boolean(
        props.booking.items[0]?.rental &&
            !props.booking?.items[0]?.rental?.isActive,
    );

    useEffect(() => {
        dispatch(fetchLocations({}));
    }, []);

    const handleFinishBooking = () => {
        const itemIds = props.booking.items.map((item) => item.id);
        dispatch(finishRental({ orderId: props.booking.id, items: itemIds }));
    };

    const handleStartBooking = () => {
        const itemIds = props.booking.items.map((item) => item.id);
        dispatch(startRental({ orderId: props.booking.id, items: itemIds }));
    };

    // The shared, translated duration label (it used to be hand-built English,
    // "-1 hours" for the windows the old visitor-booking flow wrote with the
    // end before the start). A window that is not positive reads as n/a.
    const durationLabel = formatBookedWindowLabel(
        props.booking.items[0]?.fromDate ?? props.booking.items[0]?.startDate,
        props.booking.items[0]?.toDate ?? props.booking.items[0]?.endDate,
        t,
    ) || t('common.not_applicable');

    let door = -1;
    if (
        props.booking &&
        props.booking.items[0]?.rental &&
        props.booking &&
        props.booking.items[0]?.rental.doors
    ) {
        door = props.booking.items[0]?.rental?.doors[0].door;
    }

    return (
        <div
            className={
                'bg-background border-foreground border rounded py-4 px-5 text-foreground'
            }
        >
            <header>
                <h2 className={'text-2xl font-semibold'}>
                    {t('common.booking_number')} {props.booking.bookingNumber}
                </h2>
                <h3>
                    {t('common.booking_date')}{' '}
                    {formatAtStation(props.booking.createdDate, 'dd.MM.yyyy HH:mm', 'en', timeZone)}
                </h3>
                <Chip 
                    className={'my-4'}
                    label={
                        hasBeenStarted && !hasBeenFinished
                            ? `${t('common.started')} ${formatAtStation(
                                props.booking.items[0]?.rental?.startedDate,
                                'dd.MM.yyyy HH:mm', 'en', timeZone,
                            )}`
                            : hasBeenFinished && hasBeenStarted
                                ? `${t('common.finished')} ${formatAtStation(
                                    props.booking.items[0]?.rental?.finishedAtDate,
                                    'dd.MM.yyyy HH:mm', 'en', timeZone,
                                )}`
                                : `${t('common.not_started')}`
                    }
                    size={'small'}
                    style={{
                        backgroundColor:
                            hasBeenStarted && !hasBeenFinished
                                ? '#0068ff'
                                : hasBeenFinished && hasBeenStarted
                                    ? '#f2994a'
                                    : '#ff5141',
                    }}
                />
            </header>
            <section className={'text-sm flex flex-col gap-0.5'}>
                {props.booking.contactData?.firstname &&
                    props.booking.contactData?.lastname && (
                    <p>
                        {t('common.customer')}:{' '}
                        {props.booking.contactData?.firstname}{' '}
                        {props.booking.contactData?.lastname}
                    </p>
                )}
                <a href={`mailto:${props.booking.contactData?.email}`}>
                    {props.booking.contactData?.email}
                </a>
                {props.booking.contactData?.company && (
                    <p>
                        {t('common.company')}:{' '}
                        {props.booking.contactData.company}
                    </p>
                )}
                <p>
                    <span className={'underline'}>{t('common.office')}:</span> {props.booking.locationAddress?.city}
                </p>
                <p>
                    {door !== -1
                        ? t('common.locker_size', {
                            number: door,
                            size: props.booking.lockerSize,
                        })
                        : t('common.locker_size_not_assigned', {
                            size: props.booking.lockerSize,
                        })}
                </p>
                <p>
                    {t('common.duration')}:{' '}
                    {durationLabel}
                </p>
                <p>
                    {t('common.start')}:{' '}
                    {formatAtStation(
                        props.booking.items[0]?.fromDate ??
                            props.booking.items[0]?.startDate,
                        'dd.MM.yyyy', 'en', timeZone,
                    )}
                </p>
                <p>
                    {t('common.end')}:{' '}
                    {formatAtStation(
                        props.booking.items[0]?.toDate ??
                            props.booking.items[0]?.endDate,
                        'dd.MM.yyyy', 'en', timeZone,
                    )}
                </p>
                {props.booking.comment && (
                    <p>
                        {t('visitor_booking.booking_comment')}:{' '}
                        {props.booking.comment}
                    </p>
                )}
            </section>
            <section className={'mt-4'}>
                {hasBeenStarted && !hasBeenFinished && (
                    <button
                        className={'border border-foreground text-foreground rounded py-1 px-5 '
                        + 'disabled:bg-foreground disabled:opacity-10 disabled:text-background'}
                        onClick={handleFinishBooking}
                    >
                        {t('common.finish_booking')}
                    </button>
                )}
                {!hasBeenStarted && (
                    <button
                        disabled={checkIfOrderCanBeStarted(props.booking)}
                        className={'border border-foreground text-foreground rounded py-1 px-5 '
                            + 'disabled:bg-foreground disabled:opacity-10 disabled:text-background'}
                        onClick={handleStartBooking}
                    >
                        {t('common.start_booking')}
                    </button>
                )}
            </section>
        </div>
    );
};

export default BookingInfo;
